import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useDelete } from "@refinedev/core";
import { neutral, primary } from "style/color";
import { ReactComponent as DeleteTrashIcon } from "assets/icons/delete-trash.svg";

export type FundAccountRemoveMenuTarget =
  | { kind: "member"; id: number; email: string }
  | { kind: "invitation"; id: number; email: string };

const resourceForTarget = (target: FundAccountRemoveMenuTarget) =>
  target.kind === "member"
    ? "fund-account-member"
    : "fund-account-invitation";

export const FundAccountRemoveUserMenu: React.FC<{
  target: FundAccountRemoveMenuTarget;
  onRemoved: () => void;
}> = ({ target, onRemoved }) => {
  const { mutate, isLoading } = useDelete();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const submitRemove = () => {
    mutate(
      {
        resource: resourceForTarget(target),
        id: String(target.id),
        successNotification: () => ({
          message: "User has been successfully removed from fund",
          type: "success",
        }),
      },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          onRemoved();
        },
      }
    );
  };

  return (
    <>
      <Box
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{ flexShrink: 0, ml: 1 }}
      >
        <IconButton
          onClick={handleMenuClick}
          aria-controls={menuOpen ? "fund-remove-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <MoreVertIcon sx={{ color: neutral[400] }} />
        </IconButton>
        <Menu
          id="fund-remove-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiMenu-paper": { px: 1 },
          }}
          MenuListProps={{
            onClick: (e) => e.stopPropagation(),
            onMouseDown: (e) => e.stopPropagation(),
          }}
        >
          <MenuItem
            className="contribution-dropdown"
            onClick={() => {
              setConfirmOpen(true);
              handleMenuClose();
            }}
          >
            <DeleteTrashIcon style={{ marginRight: 8 }} />
            Remove user from fund
          </MenuItem>
        </Menu>
      </Box>

      <Dialog
        sx={{ m: 2 }}
        fullWidth
        maxWidth="sm"
        open={confirmOpen}
        onClose={() => !isLoading && setConfirmOpen(false)}
      >
        <DialogTitle sx={{ mt: 3, pl: 4 }}>
          <Typography
            sx={{
              color: "neutral",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            variant="subtitle1"
          >
            Remove from this fund?
            <IconButton
              disabled={isLoading}
              onClick={() => setConfirmOpen(false)}
              sx={{
                padding: 0,
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pl: 4, mb: 3 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} sm={12}>
              <Typography sx={{ color: "neutral" }} variant="bodyCopyRegular">
                You’re about to remove{" "}
                <Typography
                  sx={{ color: primary[500] }}
                  variant="bodyCopyBold"
                  component="span"
                >
                  {target.email}
                </Typography>
                <br />
                Once removed, they won’t be able to access the fund.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} textAlign="end">
              <Button
                sx={{ mr: 2 }}
                variant="text"
                color="primary"
                size="large"
                disabled={isLoading}
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading}
                onClick={submitRemove}
              >
                Remove
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};
